import { Type } from "class-transformer";

import { DynamicType } from "../../thematics/models/dynamic.model";
import { Thematic } from "../../thematics/models/thematic.model";
import { TrainMembership } from "../../trains/models/membership.entity";
import { OrganizationPilot, Talker } from "../../users/models/talker.entity";
import { CreateUser } from "../../users/models/users.entity";

export interface ISubscriptionPlan {
  id: string;
  name: string;
  slug: string;
}

export class SubscriptionPlanVersion {
  id!: string;

  versionCode!: string;

  plan?: ISubscriptionPlan;

  @Type(() => Date)
  effectiveFrom!: Date;

  @Type(() => Date)
  effectiveTo?: Date | null;

  rulesJson!: PlanCapabilities;
}

export type CreditCosts = {
  orientationCall: number;
  speechTrain: number;
  coachingTrain: number;
};

export class SubscriptionPlanCatalogItem implements ISubscriptionPlan {
  id!: string;

  name!: string;

  slug!: string;

  @Type(() => SubscriptionPlanVersion)
  versions!: SubscriptionPlanVersion[];
}

export type PlanCapabilities = {
  be: boolean;
  resources: "none" | "linked_only" | "full";
  formations: boolean;
  journeys: boolean;
  orientation: boolean;
  statistics: boolean;
  organization_settings: boolean;
  collaborators: boolean;
  credit_policy: {
    mode: "manual_topup" | "included_per_collaborator";
    orientation_credit_cost: number;
    journey_credit_cost: number;
    included_cycle_credits_per_collaborator: number;
    per_person_journey_quota: number | null;
    cycle_anchor: "subscription_anniversary";
    cycle_months: number;
    top_up_allowed: boolean;
    commercial_terms: {
      pricing_model?: "flat" | "tiered_by_seats";
      seat_tiers?: {
        tier_1_max: number;
        tier_2_max: number;
      };
      annual_price_per_seat_cents?: {
        tier_1: number;
        tier_2: number;
        tier_3: number;
      };
      annual_subscription_price_cents?: number;
      journey_price_cents?: number;
      additional_journey_price_cents?: number;
      coaching_journey_price_cents?: number;
    };
  };
};

export class ActiveSubscription {
  id!: string;

  planCode!: string;

  versionCode!: string;

  @Type(() => Date)
  startsAt!: Date;

  @Type(() => Date)
  nextRenewalAt!: Date;

  seatCount!: number;

  status!: "active" | "cancelled";
}

export class CreditSummary {
  currentUnits!: number;

  cycleCreditsIncluded!: number;

  cycleCreditsRemaining!: number;

  includedCycleCreditsRemaining!: number;

  complementaryCreditsRemaining!: number;

  includedJourneysRemaining!: number;

  complementaryJourneysRemaining!: number;

  journeyCreditsCost!: number;

  @Type(() => Date)
  nextRenewalAt?: Date;
}

export type ResolvedPlanPolicy = {
  planCode: string;
  isLegacy: boolean;
  activeSubscription?: ActiveSubscription;
  capabilities: PlanCapabilities;
  creditSummary: CreditSummary;
  creditCosts: CreditCosts;
};

export type CreateOrganizationSubscription = {
  planVersionId: string;
  startsAt: string;
  seatCount: number;
};

export type CreateOrganizationDialogResult = {
  organization: CreateOrganization;
  subscription2026?: CreateOrganizationSubscription;
};

export type UpdateOrganizationSubscription = {
  nextRenewalAt?: string;
  seatCount?: number;
  status?: "active" | "cancelled";
};

export type PurchaseComplementaryJourneys = {
  quantity: number;
  journeyType?: DynamicType;
};

export type BillingFlowType =
  | "subscription_initial"
  | "subscription_renewal"
  | "complementary_journeys"
  | "subscription_upgrade"
  | "seat_increase";

export type BillingRecordStatus =
  | "draft"
  | "payment_pending"
  | "paid"
  | "failed"
  | "cancelled"
  | "synced";

export type BillingRecordSummary = {
  id: string;
  flowType: BillingFlowType;
  status: BillingRecordStatus;
  amountCents: number;
  currency: string;
  planCode?: string | null;
  seatCount?: number | null;
  quantity?: number | null;
  journeyType?: DynamicType | null;
  checkoutUrl?: string | null;
  providerDocumentId?: string | null;
  issuedAt?: string | null;
  paidAt?: string | null;
  activatedAt?: string | null;
  periodStartAt?: string | null;
  periodEndAt?: string | null;
  errorMessage?: string | null;
};

export type BillingStatus = {
  organizationId: string;
  sellsyThirdId?: string | null;
  nextRenewalAt?: string | null;
  latestRecord?: BillingRecordSummary | null;
  pendingSubscriptionCheckout?: BillingRecordSummary | null;
  pendingRenewalCheckout?: BillingRecordSummary | null;
  lastComplementaryJourneysCheckout?: BillingRecordSummary | null;
  activeSubscriptionChangeRequest?: SubscriptionChangeRequestSummary | null;
  records: BillingRecordSummary[];
};

export type BillingCheckoutResponse = {
  billingRecordId: string;
  checkoutUrl: string | null;
  status: BillingRecordStatus;
};

export type SubscriptionChangeType =
  | "cancellation_at_renewal"
  | "upgrade_now"
  | "seat_increase_now"
  | "downgrade_at_renewal"
  | "seat_decrease_at_renewal";

export type SubscriptionChangeStatus =
  | "draft"
  | "pending_review"
  | "payment_pending"
  | "scheduled"
  | "applied"
  | "cancelled"
  | "rejected";

export type SubscriptionChangeEffectiveMode = "immediate" | "next_renewal";

export type SubscriptionChangeRequestPayload = {
  requestType: SubscriptionChangeType;
  requestedEffectiveMode?: SubscriptionChangeEffectiveMode;
  targetPlanCode?: string;
  targetSeatCount?: number;
};

export type SubscriptionChangeSimulation = {
  requestType: SubscriptionChangeType;
  effectiveMode: SubscriptionChangeEffectiveMode;
  currentPlanCode: string;
  targetPlanCode?: string | null;
  currentSeatCount: number;
  targetSeatCount?: number | null;
  effectiveAt: string;
  estimatedAmountCents: number;
  currency: string;
  requiresPayment: boolean;
};

export type SubscriptionChangeRequestSummary = {
  id: string;
  subscriptionId: string;
  requestType: SubscriptionChangeType;
  status: SubscriptionChangeStatus;
  effectiveMode: SubscriptionChangeEffectiveMode;
  currentPlanCode: string;
  targetPlanCode?: string | null;
  currentSeatCount: number;
  targetSeatCount?: number | null;
  effectiveAt: string;
  estimatedAmountCents: number;
  currency: string;
  checkoutUrl?: string | null;
  billingRecordId?: string | null;
  requestedAt: string;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
  appliedAt?: string | null;
  cancelledAt?: string | null;
};

export type ManualCreditLot = {
  units: number;
  note?: string;
};

export class OrganizationSubscription {
  id!: string;

  organizationId!: string;

  planVersionId!: string;

  @Type(() => Date)
  startsAt!: Date;

  @Type(() => Date)
  nextRenewalAt!: Date;

  seatCount!: number;

  status!: "active" | "cancelled";

  @Type(() => SubscriptionPlanVersion)
  planVersion?: SubscriptionPlanVersion;
}

export class OrganizationCreditLot {
  id!: string;

  organizationId!: string;

  subscriptionId?: string | null;

  source!: string;

  grantedUnits!: number;

  remainingUnits!: number;

  @Type(() => Date)
  grantedAt!: Date;

  @Type(() => Date)
  expiresAt?: Date | null;

  metadata?: Record<string, unknown> | null;
}

export interface IOrganization {
  id: string;
  name: string;
  units: number;
  createdAt: Date;
  talkerQuota: number | null;
  members?: Talker[];
  parent?: IOrganization;
  children?: IOrganization[];
  subscriptionPlan?: ISubscriptionPlan;
  testEndDate?: string;
  defaultLang?: { id: string; name: string } | null;
  activeSubscription?: ActiveSubscription;
  capabilities?: PlanCapabilities;
  creditSummary?: CreditSummary;
  creditCosts?: CreditCosts;
}

export enum TransactionType {
  ADMIN_MODIFICATION = "admin-modification",
  TRAIN_MEMBERSHIP = "train-membership",
  ORGANIZATION_TRANSFER = "organization-transfer",
  ORIENTATION_CALL = "orientation-call",
  SPEECH_TRAIN_MEMBERSHIP = "speech-train-membership",
  COACHING_TRAIN_MEMBERSHIP = "coaching-train-membership",
  FORMATION_MEMBERSHIP = "formation-membership",
  COMPLEMENTARY_JOURNEY_PURCHASE = "complementary-journey-purchase",
}

export type OrganizationTransactionParsed = {
  id: string;
  date: Date;
  units: string;
  restAmount: number;
  description: string;
};

export class Transaction {
  id!: string;

  @Type(() => Date)
  createdAt!: Date;

  units!: number;

  @Type(() => Organization)
  sender?: Organization;

  @Type(() => Organization)
  receiver?: Organization;

  type!: TransactionType;

  description!: string;
}

export class Organization implements IOrganization {
  id!: string;

  name!: string;

  units!: number;

  @Type(() => Date)
  createdAt!: Date;

  extraOrganizationMatching: boolean = true;

  intraOrganizationMatching: boolean = false;

  yearlyTalkerQuota: boolean = false;

  talkerQuota: number | null = 1;

  get nameWithUnits(): string {
    return `${this.name.toUpperCase()} (${this.units} crédit${
      this.units > 1 ? "s" : ""
    } disponible${this.units > 1 ? "s" : ""})`;
  }

  @Type(() => Talker)
  members!: Talker[];

  @Type(() => OrganizationPilot)
  pilotAssociations?: OrganizationPilot[];

  get pilots(): Talker[] {
    return (
      this.pilotAssociations
        ?.map((association) => association.pilot)
        .filter((pilot): pilot is Talker => !!pilot) ?? []
    );
  }

  @Type(() => Transaction)
  unitsIn?: Transaction[];

  @Type(() => Transaction)
  unitsOut?: Transaction[];

  @Type(() => Organization)
  parent?: Organization;

  @Type(() => Organization)
  children?: Organization[];

  @Type(() => Organization)
  forbiddenOrganizations?: Organization[];

  @Type(() => Thematic)
  forbiddenThematics?: Thematic[];

  /**
   * Dynamic types that are disabled for this organization.
   * Talkers in this org cannot access parcours with these dynamics.
   * Empty array = all dynamics enabled (default).
   */
  forbiddenDynamicTypes?: DynamicType[];

  subscriptionPlan?: ISubscriptionPlan;

  testEndDate?: string;

  defaultLang?: { id: string; name: string } | null;

  @Type(() => ActiveSubscription)
  activeSubscription?: ActiveSubscription;

  @Type(() => OrganizationSubscription)
  subscriptions?: OrganizationSubscription[];

  capabilities?: PlanCapabilities;

  @Type(() => CreditSummary)
  creditSummary?: CreditSummary;

  creditCosts?: CreditCosts;

  get forbiddenThematicIds(): string[] | undefined {
    return this.forbiddenThematics?.map((thematic) => thematic.id);
  }

  /**
   * Check if a specific dynamic type is forbidden for this organization.
   */
  isDynamicTypeForbidden(type: DynamicType): boolean {
    return this.forbiddenDynamicTypes?.includes(type) ?? false;
  }
}

export type OrganizationTrainRules = {
  intraOrganizationMatching: boolean;
  extraOrganizationMatching: boolean;
  talkerQuota: number | null;
  forbiddenThematicIds?: string[];
  forbiddenDynamicTypes?: DynamicType[];
  forbiddenOrganizationIds?: string[];
  allowedOrganizationIds?: string[];
};

export type CreateOrganization = OrganizationTrainRules & {
  name: string;
  units: number;
  addUnitsFrom?: string;
  pilots?: CreateUser[];
  pilotId?: string;
  parent?: string;
  children?: string[];
  subscriptionPlan?: string;
  yearlyTalkerQuota?: boolean;
  testEndDate?: string;
  defaultLangId?: string | null;
};

export type OrganizationTrainRulesForm = {
  intraOrganizationMatching: boolean;
  talkerQuota: number | null;
};

export type CreateOrganizationForm = {
  name: string;
  gotUnits: boolean;
  units: number;
  addUnitsFrom: string;
  intraOrganizationMatching: boolean;
  gotQuota: boolean;
  talkerQuota: number | null;
  gotPilot: boolean;
  gotFormula: boolean;
  pilot: string;
  gotParent: boolean;
  parent: string;
  gotChildren: boolean;
  gotForbiddenThematics: boolean;
  gotForbiddenOrganizations: boolean;
  subscriptionPlan?: string;
  testEndDate?: string;
};

export class OrganizationTrainMembership {
  id!: string;

  @Type(() => TrainMembership)
  trainMemberships!: TrainMembership[];

  @Type(() => TrainMembership)
  firstTrainMemberships!: TrainMembership[];
}

export type OrganizationDataResume = {
  quotaDemands: number;
  unitDemands: number;
  members: number;
  activatedMembers: number;
  talkers: number;
  trainSatisfactionPercent?: number;
  relativesRecommendationPercent?: number;
  goalsAchievementPercent?: number;
  lifeSatisfactionScore: number;
  workSatisfactionScore: number;
  affectAndRelationScore: number;
  canSeeStats: boolean;
};

export type OrganizationToDeleteInfo = Organization & {
  siblings: Organization[];
};

export interface IOrganizationTransfer {
  fromOrganizationId: string;
  destinationOrganizationId: string;
}
